import {
  TDeviceStatusAnalyticsData,
  TIntegrationsCoveragePerIntegrationData,
  TWifiHealthAnalyticsData,
} from "src/data/portfolioAnalytics/types/portfolioAnalyticsTypes"

/**
 * Calculates the total number of units and linked units from integrations analytics data.
 * @param analytics - The analytics array from integrations coverage API response
 * @returns An object with totalUnits and linkedUnits
 */
export function getIntegrationsCoverageTotals(
  analytics: Array<{
    data: TIntegrationsCoveragePerIntegrationData[]
  }>
): { totalUnits: number; linkedUnits: number } {
  return analytics.reduce(
    (acc, group) => {
      const groupTotals = group.data.reduce(
        (groupAcc, integration) => {
          const { total_integration_entities, linked_homes } =
            integration.aggregation
          return {
            totalUnits: groupAcc.totalUnits + total_integration_entities,
            linkedUnits: groupAcc.linkedUnits + linked_homes,
          }
        },
        { totalUnits: 0, linkedUnits: 0 }
      )
      return {
        totalUnits: acc.totalUnits + groupTotals.totalUnits,
        linkedUnits: acc.linkedUnits + groupTotals.linkedUnits,
      }
    },
    { totalUnits: 0, linkedUnits: 0 }
  )
}

/**
 * Calculates the number of units for each device status using reduce.
 * @param data - Array of TDeviceStatusAnalyticsData
 * @returns Object with counts for allOnline, someOffline, allOffline, and noDevices
 */
export function getDeviceCoverage(data: TDeviceStatusAnalyticsData[]): {
  allOnline: number
  someOffline: number
  allOffline: number
  noDevices: number
} {
  return data.reduce(
    (acc, item) => ({
      allOnline: acc.allOnline + item.aggregation.all_online,
      someOffline: acc.someOffline + item.aggregation.some_offline,
      allOffline: acc.allOffline + item.aggregation.all_offline,
      noDevices: acc.noDevices + item.aggregation.no_devices,
    }),
    { allOnline: 0, someOffline: 0, allOffline: 0, noDevices: 0 }
  )
}

/**
 * Calculates the number of units for each wifi quality status using reduce.
 * @param data - Array of TWifiHealthAnalyticsData
 * @returns Object with counts for excellent, good, fair, poor, none, and noDevices
 */
export function getWifiHealthCoverage(data: TWifiHealthAnalyticsData[]): {
  excellent: number
  good: number
  fair: number
  poor: number
  none: number
  noDevices: number
} {
  return data.reduce(
    (acc, item) => ({
      excellent: acc.excellent + item.aggregation.excellent,
      good: acc.good + item.aggregation.good,
      fair: acc.fair + item.aggregation.fair,
      poor: acc.poor + item.aggregation.poor,
      none: acc.none + item.aggregation.none,
      noDevices: acc.noDevices + item.aggregation.no_devices,
    }),
    { excellent: 0, good: 0, fair: 0, poor: 0, none: 0, noDevices: 0 }
  )
}
